import { Box, Button } from "@justworkshr/milo-core";
import { InputMessage, Label } from "@justworkshr/milo-form";
import { FieldArray, getIn, useFormikContext } from "formik";
import styles from "./AutoFormFieldArray.module.css";
import { ArrayFieldDataType, FieldDataType, FieldTypes } from "./types";
import isEqual from "lodash/isEqual";
import range from "lodash/range";
import AutoFormField from "./AutoFormField";
import { useState } from "react";

const {
  row,
  message,
  rowCard,
  rowSimple,
  fieldCard,
  itemHeader,
  fieldSimple,
  removeButton,
  addMoreButton,
  arrayContainer,
  itemHeaderCard,
} = styles;

type Props = ArrayFieldDataType & {
  fields?: FieldDataType<FieldTypes>[];
};

const defaultValues: {
  [key in FieldTypes]: unknown;
} = {
  [FieldTypes.phoneNumber]: { type: "", value: "" },
  [FieldTypes.text]: "",
  [FieldTypes.number]: "",
  [FieldTypes.date]: "",
  [FieldTypes.select]: "",
  [FieldTypes.checkbox]: "",
  [FieldTypes.object]: "",
  [FieldTypes.boolean]: "",
  [FieldTypes.sensitiveText]: "",
  [FieldTypes.group]: "",
  [FieldTypes.address]: {
    type: "",
    address1: "",
    address2: "",
    city: "",
    zoneCode: "",
    postalCode: "",
    countryCode: "",
  },
  [FieldTypes.multiSelect]: "",
  [FieldTypes.file]: "",
};

function defaultValue(type: FieldTypes, fields?: FieldDataType<FieldTypes>[]) {
  if (type === FieldTypes.object) {
    return fields?.reduce((acc, field) => {
      acc[field.name] = defaultValues[field.type];
      return acc;
    }, {} as Record<string, unknown>);
  }

  return defaultValues[type];
}

export default function AutoFormFieldArray(props: Props) {
  const [showItems, setShowItems] = useState(props.array.showFirstItem ?? true);
  const { type, name, fields, array } = props;
  const addButtonLabel = "Add more";
  const limit = array.limit;
  const getIndexedName = (name: string, index: number) => `${name}.${index}`;

  const { values } = useFormikContext();
  // TODO: Improve typing here.
  const valuesArray: string[] = getIn(values, name);
  const valuesArrayLength = Math.max(valuesArray.length, 1);

  const isMoreThanLimit = valuesArrayLength >= limit;
  const isAllFilled = valuesArray.every(
    (value) => !isEqual(defaultValue(type, fields), value)
  );
  const isAddMoreDisabled = !isAllFilled || isMoreThanLimit;

  const isObjectArray = type === FieldTypes.object;

  // Toggle between CSS classes for object and non-object arrays.
  const cssConfig = isObjectArray
    ? {
        row: rowCard,
        box: {
          border: { borderColor: "neutral", borderWidth: "sm" },
          padding: "md",
        } as const,
        itemHeader: itemHeaderCard,
        field: fieldCard,
      }
    : {
        row: rowSimple,
        box: {},
        itemHeader: {},
        field: fieldSimple,
      };

  return (
    <div className={arrayContainer}>
      <Label>{props.label}</Label>
      {props.message ? (
        <div className={message}>
          <InputMessage type="help">{props.message}</InputMessage>
        </div>
      ) : null}
      <FieldArray
        name={name}
        render={(arrayHelpers) => {
          const handleRemove = (index: number) => () => {
            valuesArray.length < 2 && setShowItems(false);
            arrayHelpers.remove(index);
          };

          const handleAddMore = () => {
            setShowItems(true);
            arrayHelpers.push(defaultValue(type, fields));
          };

          const showRemoveButton = (index: number) => {
            if (index === 0 && !showItems) return false;
            if (typeof array === "object" && array.deletionStrategy === "any") {
              return true;
            }
            return valuesArray.length > 1 && index === valuesArray.length - 1;
          };

          return (
            <>
              {showItems &&
                range(0, valuesArrayLength).map((index) => {
                  const indexedName = getIndexedName(name, index);
                  return (
                    <Box key={indexedName} {...cssConfig.box}>
                      <div className={[row, cssConfig.row].join(" ")}>
                        <div className={cssConfig.field}>
                          <AutoFormField
                            {...props}
                            name={indexedName}
                            array={undefined}
                            message={undefined}
                          />
                        </div>

                        <div
                          className={[itemHeader, cssConfig.itemHeader].join(
                            " "
                          )}
                        >
                          {isObjectArray && array.itemLabelSuffix && (
                            <h3 style={{ flexGrow: "1" }}>{`${
                              array.itemLabelSuffix
                            } ${index + 1}`}</h3>
                          )}
                          {showRemoveButton(index) && (
                            <div className={removeButton}>
                              <Button
                                variant="ghost"
                                type="button"
                                onClick={handleRemove(index)}
                                leftIcon="trash"
                                children={null}
                                data-testid="array-remove-button"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </Box>
                  );
                })}
              <div className={addMoreButton}>
                <Button
                  variant="outlined"
                  type="button"
                  onClick={handleAddMore}
                  disabled={isAddMoreDisabled}
                >
                  {addButtonLabel}
                </Button>
              </div>
            </>
          );
        }}
      />
    </div>
  );
}
